import { Navigate } from "react-router-dom";
import type { RouteObject } from "react-router-dom";
import type { ComponentType } from "react";
import type { TPermissionKey } from "@agt/contract";
import { AuthGuard, GuestGuard } from "@/features/auth/components/AuthGuard";
import { V2Access } from "@/components/v2/V2Layouts";

function screen(
  load: () => Promise<ComponentType>,
  permission?: TPermissionKey,
  masterOnly = false,
): RouteObject {
  return {
    lazy: async () => {
      const Component = await load();
      return {
        Component: () => (
          <V2Access permission={permission} masterOnly={masterOnly}>
            <Component />
          </V2Access>
        ),
      };
    },
  };
}

export const v2Route: RouteObject = {
  path: "v2",
  lazy: async () => ({
    Component: (await import("@/components/v2/V2Layouts")).V2RootLayout,
  }),
  children: [
    { index: true, element: <Navigate to="/v2/dashboard" replace /> },
    {
      element: <GuestGuard />,
      children: [
        {
          lazy: async () => ({
            Component: (await import("@/components/v2/V2Layouts")).V2AuthLayout,
          }),
          children: [
            {
              path: "login",
              lazy: async () => ({
                Component: (await import("@/pages/v2/auth")).V2LoginPage,
              }),
            },
            {
              path: "reset-password/:token",
              lazy: async () => ({
                Component: (await import("@/pages/v2/auth"))
                  .V2ResetPasswordPage,
              }),
            },
          ],
        },
      ],
    },
    {
      element: <AuthGuard />,
      children: [
        {
          lazy: async () => ({
            Component: (await import("@/components/v2/V2Layouts")).V2AppLayout,
          }),
          children: [
            {
              path: "dashboard",
              ...screen(() =>
                import("@/pages/v2/dashboard").then((module) => module.default),
              ),
            },
            {
              path: "master-data/client",
              ...screen(
                () =>
                  import("@/pages/v2/master-data").then(
                    (module) => module.V2ClientPage,
                  ),
                "client_company:read",
              ),
            },
            {
              path: "master-data/vendor",
              ...screen(
                () =>
                  import("@/pages/v2/master-data").then(
                    (module) => module.V2VendorPage,
                  ),
                "vendor:read",
              ),
            },
            {
              path: "master-data/user",
              ...screen(
                () =>
                  import("@/pages/v2/master-data").then(
                    (module) => module.V2UserPage,
                  ),
                "user:read",
              ),
            },
            {
              path: "master-data/account",
              ...screen(
                () =>
                  import("@/pages/v2/master-data").then(
                    (module) => module.V2AccountPage,
                  ),
                "account:read",
              ),
            },
            {
              path: "master-data/contact",
              ...screen(
                () =>
                  import("@/pages/v2/master-data").then(
                    (module) => module.V2ContactPage,
                  ),
                "contact:read",
              ),
            },
            {
              path: "master-data/customer",
              ...screen(
                () =>
                  import("@/pages/v2/master-data").then(
                    (module) => module.V2CustomerPage,
                  ),
                "customer:read",
              ),
            },
            {
              path: "settings/general",
              ...screen(
                () =>
                  import("@/pages/v2/settings").then(
                    (module) => module.V2GeneralSettingsPage,
                  ),
                "settings:read",
              ),
            },
            {
              path: "settings/payment-methods",
              ...screen(
                () =>
                  import("@/pages/v2/settings").then(
                    (module) => module.V2PaymentMethodsPage,
                  ),
                "payment_method:read",
              ),
            },
            {
              path: "settings/roles",
              ...screen(
                () =>
                  import("@/pages/v2/settings").then(
                    (module) => module.V2RolesPage,
                  ),
                "role_permission:read",
              ),
            },
            {
              path: "financial/transaction",
              ...screen(
                () =>
                  import("@/pages/v2/financial").then(
                    (module) => module.V2TransactionPage,
                  ),
                "transaction:read",
              ),
            },
            {
              path: "financial/bill",
              ...screen(
                () =>
                  import("@/pages/v2/financial").then(
                    (module) => module.V2BillPage,
                  ),
                "bill:read",
              ),
            },
            {
              path: "financial/account-receivable",
              ...screen(
                () =>
                  import("@/pages/v2/financial").then(
                    (module) => module.V2AccountReceivablePage,
                  ),
                "account_receivable:read",
              ),
            },
            {
              path: "financial/account-payable",
              ...screen(
                () =>
                  import("@/pages/v2/financial").then(
                    (module) => module.V2AccountPayablePage,
                  ),
                "account_payable:read",
              ),
            },
            {
              path: "financial/invoice",
              ...screen(
                () =>
                  import("@/pages/v2/financial").then(
                    (module) => module.V2InvoiceListPage,
                  ),
                "invoice:read",
              ),
            },
            {
              path: "financial/invoice/create",
              ...screen(
                () =>
                  import("@/pages/v2/financial").then(
                    (module) => module.V2InvoiceCreatePage,
                  ),
                "invoice:create",
              ),
            },
            {
              path: "financial/invoice/:id",
              ...screen(
                () =>
                  import("@/pages/v2/financial").then(
                    (module) => module.V2InvoiceDetailPage,
                  ),
                "invoice:update",
              ),
            },
            {
              path: "financial/journal",
              ...screen(
                () =>
                  import("@/pages/v2/financial").then(
                    (module) => module.V2JournalPage,
                  ),
                "journal:read",
              ),
            },
            {
              path: "financial/journal/new",
              ...screen(
                () =>
                  import("@/pages/v2/financial").then(
                    (module) => module.V2JournalNewPage,
                  ),
                "journal:create",
              ),
            },
            {
              path: "financial/journal/:id",
              ...screen(
                () =>
                  import("@/pages/v2/financial").then(
                    (module) => module.V2JournalDetailPage,
                  ),
                "journal:read",
              ),
            },
            {
              path: "financial/journal/:id/edit",
              ...screen(
                () =>
                  import("@/pages/v2/financial").then(
                    (module) => module.V2JournalEditPage,
                  ),
                "journal:update",
              ),
            },
            {
              path: "financial/petty-cash",
              ...screen(
                () =>
                  import("@/pages/v2/financial").then(
                    (module) => module.V2PettyCashPage,
                  ),
                undefined,
                true,
              ),
            },
            {
              path: "*",
              element: (
                <section className="v2-error">
                  <h1>Page not found</h1>
                  <p>Choose a page from the workspace navigation.</p>
                </section>
              ),
            },
          ],
        },
        {
          lazy: async () => ({
            Component: (await import("@/components/v2/V2Layouts"))
              .V2ExportLayout,
          }),
          children: [
            {
              path: "export/invoice/:id",
              ...screen(
                () =>
                  import("@/pages/export/invoice/[id]").then(
                    (module) => module.default,
                  ),
                "invoice:read",
              ),
            },
            {
              path: "export/bill/:id",
              ...screen(
                () =>
                  import("@/pages/export/bill/[id]").then(
                    (module) => module.default,
                  ),
                "bill:read",
              ),
            },
            {
              path: "export/ar/:id",
              ...screen(
                () =>
                  import("@/pages/export/ar/[id]").then(
                    (module) => module.default,
                  ),
                "account_receivable:read",
              ),
            },
            {
              path: "export/ap/:id",
              ...screen(
                () =>
                  import("@/pages/export/ap/[id]").then(
                    (module) => module.default,
                  ),
                "account_payable:read",
              ),
            },
          ],
        },
      ],
    },
  ],
};
